import {
  ArrowsExpandIcon,
  PlayIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import React, { useCallback, useEffect, useState } from 'react';
import { NodeExecutionStatus } from 'types';

import { useWorkflowStore } from '@/lib/workflows/store';

import Modal from '@/components/atoms/Modal';
import Button from '@/components/buttons/Button';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { TipTapPromptEditor } from '../TipTapPromptEditor';

interface TextGenerationNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
  readOnly?: boolean;
}

// Model list lives in @/constants/workflowTextModels (shared with the
// /api/generate-text allowlist).
import {
  resolveWorkflowTextModel,
  TEXT_MODELS,
} from '@/constants/workflowTextModels';

export { TEXT_MODELS };

export function TextGenerationNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
  readOnly,
}: TextGenerationNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  const [showPreview, setShowPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState<'prompt' | 'result'>('prompt');
  const [nodeTab, setNodeTab] = useState<'prompt' | 'result'>(
    data.result ? 'result' : 'prompt',
  );

  const executionStatus = (() => {
    const s = (data as { status?: unknown })?.status;
    return typeof s === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(s)
      ? (s as NodeExecutionStatus)
      : undefined;
  })();

  const isRunning = executionStatus === 'running';

  // Auto-switch to result tab when result is generated
  useEffect(() => {
    if (data.result) {
      setNodeTab('result');
    }
  }, [data.result]);

  // Hide result if running
  const shouldShowResult = typeof data.result !== 'undefined' && !isRunning;

  const config = (data.config || {}) as {
    model?: string;
    prompt?: string;
    systemPrompt?: string;
  };

  const selectedModel =
    resolveWorkflowTextModel(config.model) || TEXT_MODELS[0].value;

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    updateNodeData(id, {
      config: {
        ...config,
        model: e.target.value,
      },
    });
  };

  const handlePromptChange = (value: string) => {
    updateNodeData(id, {
      config: {
        ...config,
        prompt: value,
      },
    });
  };

  const handleSystemPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    e.stopPropagation();
    updateNodeData(id, {
      config: {
        ...config,
        systemPrompt: e.target.value,
      },
    });
  };

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        return {
          id: sourceNode?.id || '',
          label: (sourceNode?.data?.label as string) || 'Node',
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  const rawError = (data as any)?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  const resultText =
    typeof (data as any)?.result === 'string'
      ? ((data as any).result as string)
      : Array.isArray((data as any)?.result) ||
          (typeof (data as any)?.result === 'object' &&
            (data as any)?.result !== null)
        ? JSON.stringify((data as any)?.result, null, 2)
        : typeof (data as any)?.result !== 'undefined'
          ? String((data as any)?.result)
          : '';

  return (
    <>
      <BaseNode
        title={
          (typeof (data as { label?: unknown })?.label === 'string' &&
            (data as { label?: string }).label) ||
          t('Text Generation')
        }
        icon={
          <svg
            className='w-4 h-4 text-teal-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={executionStatus}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        readOnly={readOnly}
        customHeaderButtons={
          <SimpleTooltip title={t('Preview')} side='top'>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewTab(data.result ? 'result' : 'prompt');
                setShowPreview(true);
              }}
              className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
            >
              <ArrowsExpandIcon className='w-5 h-5 text-gray-600 dark:text-gray-300' />
            </button>
          </SimpleTooltip>
        }
        customInputs={
          <>
            {createInputHandle('text', 'prompt', 30)}
            {createInputHandle('image', 'image', 70)}
          </>
        }
        customOutputs={createOutputHandle('text', 'text', 50)}
      >
        <div className='w-[400px]'>
          {/* Tab Toggle */}
          <div className='flex border-b border-gray-200 dark:border-gray-700 px-4'>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNodeTab('prompt');
              }}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                nodeTab === 'prompt'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('Prompt')}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNodeTab('result');
              }}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                nodeTab === 'result'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('Result')}
            </button>
          </div>

          {/* Prompt Tab */}
          {nodeTab === 'prompt' && (
            <div className='space-y-3 px-4 pt-3 pb-3'>
              {/* Model Selection */}
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Model')}
                </label>
                <select
                  value={selectedModel}
                  onChange={handleModelChange}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  disabled={readOnly}
                >
                  {TEXT_MODELS.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* System Prompt */}
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('System Prompt (optional)')}
                </label>
                <textarea
                  value={config.systemPrompt || ''}
                  onChange={handleSystemPromptChange}
                  placeholder={t('You are a helpful AI assistant...')}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  disabled={readOnly}
                  rows={5}
                />
              </div>

              {/* Prompt */}
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Prompt')}
                </label>
                <div className='max-h-[200px] overflow-y-auto rounded-md nowheel'>
                  <TipTapPromptEditor
                    value={config.prompt || ''}
                    onChange={readOnly ? () => {} : handlePromptChange}
                    connectedNodes={getConnectedTextNodes()}
                    placeholder={t(
                      'Enter your prompt... Type @ to reference connected nodes',
                    )}
                  />
                </div>
                <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                  {t('Type @ to reference connected nodes')}
                </p>
              </div>

              {/* Run Button */}
              <div className='pt-1'>
                <Button
                  className='w-full justify-center'
                  variant='outline'
                  isLoading={isRunning}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRunNode?.(nodeId || id);
                  }}
                >
                  {data.result ? (
                    <>
                      <RefreshIcon className='w-4 h-4 mr-2' />
                      <span>{t('Regenerate')}</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className='w-4 h-4 mr-2' />
                      <span>{t('Run')}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Result Tab */}
          {nodeTab === 'result' && (
            <div className='px-4 pt-3 pb-3'>
              {shouldShowResult && resultText ? (
                <div className='relative'>
                  <div className='w-full p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md text-sm whitespace-pre-wrap break-words text-gray-900 dark:text-white select-text cursor-text max-h-[300px] overflow-y-auto nowheel'>
                    {resultText}
                  </div>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      try {
                        if (
                          typeof navigator !== 'undefined' &&
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard.writeText(resultText);
                        } else if (typeof document !== 'undefined') {
                          const textarea = document.createElement('textarea');
                          textarea.value = resultText;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textarea);
                        }
                      } catch (err) {
                        console.error('Failed to copy text', err);
                      }
                    }}
                    className='absolute top-2 right-2 px-2 py-1 text-[10px] font-medium rounded bg-white/80 dark:bg-gray-900/80 text-primary-600 dark:text-primary-300 border border-primary-300 dark:border-primary-500 shadow-sm hover:bg-primary-50 dark:hover:bg-primary-900/40'
                  >
                    {t('Copy')}
                  </button>
                </div>
              ) : (
                <div className='py-8 text-center text-gray-400 dark:text-gray-500 text-xs'>
                  {t('No result yet. Switch to Prompt tab to run.')}
                </div>
              )}

              {/* Error Display */}
              {errorText && (
                <div className='mt-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded'>
                  <p className='text-xs text-red-600 dark:text-red-400'>
                    {errorText}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </BaseNode>

      {/* Preview Modal */}
      <Modal
        showModal={showPreview}
        onClose={() => setShowPreview(false)}
        className='sm:max-w-4xl'
      >
        <div className='p-6 space-y-4'>
          <div className='flex items-center justify-between'>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
              {(typeof (data as { label?: unknown })?.label === 'string' &&
                (data as { label?: string }).label) ||
                t('Text Generation')}
            </h2>
          </div>

          {/* Tab Toggle */}
          <div className='flex border-b border-gray-200 dark:border-gray-700'>
            <button
              onClick={() => setPreviewTab('prompt')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                previewTab === 'prompt'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('Prompt')}
            </button>
            <button
              onClick={() => setPreviewTab('result')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                previewTab === 'result'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('Result')}
              {shouldShowResult && resultText ? '' : ` (${t('empty')})`}
            </button>
          </div>

          {/* Prompt Tab */}
          {previewTab === 'prompt' && (
            <div className='space-y-4'>
              {/* Model Selection */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Model')}
                </label>
                <select
                  value={selectedModel}
                  onChange={handleModelChange}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  disabled={readOnly}
                >
                  {TEXT_MODELS.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* System Prompt */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('System Prompt (optional)')}
                </label>
                <textarea
                  value={config.systemPrompt || ''}
                  onChange={handleSystemPromptChange}
                  placeholder={t('You are a helpful AI assistant...')}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  disabled={readOnly}
                  rows={6}
                />
              </div>

              {/* Prompt */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Prompt')}
                </label>
                <TipTapPromptEditor
                  value={config.prompt || ''}
                  onChange={readOnly ? () => {} : handlePromptChange}
                  connectedNodes={getConnectedTextNodes()}
                  placeholder={t(
                    'Enter your prompt... Type @ to reference connected nodes',
                  )}
                  className='!min-h-[200px]'
                />
                <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                  {t('Type @ to reference connected nodes')}
                </p>
              </div>

              {/* Run Button */}
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>{t('Regenerate')}</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>{t('Run')}</span>
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Result Tab */}
          {previewTab === 'result' && (
            <div className='space-y-4'>
              {shouldShowResult && resultText ? (
                <div className='w-full p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md text-sm whitespace-pre-wrap break-words text-gray-900 dark:text-white max-h-[60vh] overflow-y-auto select-text cursor-text'>
                  {resultText}
                </div>
              ) : (
                <div className='py-12 text-center text-gray-400 dark:text-gray-500 text-sm'>
                  {t('No result yet. Run the node from the Prompt tab.')}
                </div>
              )}

              {/* Error Display */}
              {errorText && (
                <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 rounded'>
                  <p className='text-sm text-red-600 dark:text-red-400'>
                    {errorText}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
